import { styled, Box } from '@cad-arc';
import { colors } from '../../../colors/colors';
import { useEffect, useState } from 'react';
import priorityFlashImage from '../../../assets/images/priority_flash.png';

const animationDuration = 2000;

/**
 * Calculates the animation delay needed to synchronize all instances of the flashing animation.
 * Returns a negative delay based on the current position in the animation cycle.
 */
const calculateSyncDelay = (): string => {
    const now = Date.now();
    const offset = now % animationDuration;
    return `-${offset}ms`;
};

export const PriorityBoxCssStyled = styled(Box)<{ $flashing: boolean; $delay: string }>`
    height: 100%;
    width: 100%;
    background-color: ${(props) => (props.$flashing ? colors.queuePriority : 'transparent')};
    border-radius: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    ${(props) =>
        props.$flashing
            ? `animation: priorityFlashingAnimation ${animationDuration}ms ease-in-out infinite; animation-delay: ${props.$delay}`
            : ''}
`;

/**
 * A styled Box component that flashes if the $flashing prop is true.
 *
 * This uses pure css for the animation.
 *
 * @param {Object} props - React component props.
 * @param {boolean} props.$flashing - Whether the component should flash.
 * @param {string} props.$delay - The animation delay needed to synchronize the flashing animation across all components.
 */

export const PriorityBoxCss = ({
    children,
    isFlashing,
}: {
    children: React.ReactNode;
    isFlashing: boolean;
}) => {
    const [delay, setDelay] = useState('0s');

    useEffect(() => {
        /*
            To synchronize the flashing animation across all components, we set a delay based on the current time's position
            within the animation cycle. This ensures that all components start flashing at the same time, regardless of when
            they are loaded, when isFlashing changes, or when children change.
        */
        if (isFlashing) {
            setDelay(calculateSyncDelay());
        }
    }, [isFlashing, children]);

    return (
        <PriorityBoxCssStyled $flashing={isFlashing} $delay={delay}>
            {children}
        </PriorityBoxCssStyled>
    );
};

export const PriorityBoxStyled = styled(Box)<{ $flashing: boolean; }>`
    height: 100%;
    width: 100%;
    border-radius: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    ${(props) => (props.$flashing ? `background-image: url(${priorityFlashImage});` : ``)}
`;

/**
 * A styled Box component that flashes if the $flashing prop is true.
 *
 * This uses an animated png for the animation.
 *
 * @param {Object} props - React component props.
 * @param {boolean} props.$flashing - Whether the component should flash.
 */
export const PriorityBox = ({
    children,
    isFlashing,
}: {
    children: React.ReactNode;
    isFlashing: boolean;
}) => {
    return (
        <PriorityBoxStyled $flashing={isFlashing}>
            {children}
        </PriorityBoxStyled>
    );
};
